import { supabase } from '../lib/supabase'
import {
  LearningModule,
  LearningVideo,
  QuizQuestion,
  VideoProgress,
  QuizAttempt,
  QuizResult,
  LearningAchievement,
  UserLearningProgress,
  ModuleProgress,
  UpdateVideoProgressRequest,
  SubmitQuizRequest,
  XP_REWARDS,
  LEVEL_THRESHOLDS
} from '../types/learning'

class LearningService {
  // Module Methods
  async getModules(): Promise<LearningModule[]> {
    try {
      const { data, error } = await supabase
        .from('learning_modules')
        .select(`
          *,
          learning_videos (
            id,
            title,
            duration,
            difficulty,
            category,
            xp_reward,
            order_index,
            is_published
          )
        `)
        .eq('is_published', true)
        .order('order_index')

      if (error) throw error

      return data || []
    } catch (error) {
      console.error('Error fetching learning modules:', error)
      throw new Error('Failed to fetch learning modules')
    }
  }

  async getModuleById(moduleId: string): Promise<LearningModule | null> {
    try {
      const { data, error } = await supabase
        .from('learning_modules')
        .select(`
          *,
          learning_videos (
            *,
            user_video_progress!left (
              watched_seconds,
              completed,
              completed_at
            )
          )
        `)
        .eq('id', moduleId)
        .eq('is_published', true)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching module:', error)
      return null
    }
  }

  // Video Methods
  async getModuleVideos(moduleId: string, userId?: string): Promise<LearningVideo[]> {
    try {
      let query = supabase
        .from('learning_videos')
        .select(`
          *,
          learning_modules (
            id,
            title,
            difficulty
          )
        `)
        .eq('module_id', moduleId)
        .eq('is_published', true)
        .order('order_index')

      if (userId) {
        query = query.select(`
          *,
          learning_modules (
            id,
            title,
            difficulty
          ),
          user_video_progress!left (
            watched_seconds,
            completed,
            completed_at,
            last_watched_at
          )
        `)
      }

      const { data, error } = await query

      if (error) throw error

      // Calculate progress for each video
      const videos = (data || []).map(video => ({
        ...video,
        progress: video.user_video_progress?.[0] ? {
          ...video.user_video_progress[0],
          completion_percentage: video.duration 
            ? Math.min(100, Math.round((video.user_video_progress[0].watched_seconds / video.duration) * 100))
            : 0,
          time_remaining: video.duration 
            ? Math.max(0, video.duration - video.user_video_progress[0].watched_seconds)
            : 0
        } : undefined
      }))

      return videos
    } catch (error) {
      console.error('Error fetching module videos:', error)
      throw new Error('Failed to fetch module videos')
    }
  }

  async getVideoById(videoId: string, userId?: string): Promise<LearningVideo | null> {
    try {
      let query = supabase
        .from('learning_videos')
        .select(`
          *,
          learning_modules (
            id,
            title,
            difficulty
          ),
          quiz_questions (
            *
          )
        `)
        .eq('id', videoId)
        .eq('is_published', true)

      if (userId) {
        query = query.select(`
          *,
          learning_modules (
            id,
            title,
            difficulty
          ),
          quiz_questions (
            *
          ),
          user_video_progress!left (
            watched_seconds,
            completed,
            completed_at,
            last_watched_at
          )
        `)
      }

      const { data, error } = await query.single()

      if (error) throw error

      if (!data) return null

      // Calculate progress
      const progress = data.user_video_progress?.[0] ? {
        ...data.user_video_progress[0],
        completion_percentage: data.duration 
          ? Math.min(100, Math.round((data.user_video_progress[0].watched_seconds / data.duration) * 100))
          : 0,
        time_remaining: data.duration 
          ? Math.max(0, data.duration - data.user_video_progress[0].watched_seconds)
          : 0
      } : undefined

      return {
        ...data,
        progress
      }
    } catch (error) {
      console.error('Error fetching video:', error)
      return null
    }
  }

  // Progress Methods
  async updateVideoProgress(request: UpdateVideoProgressRequest): Promise<void> {
    try {
      const { video_id, watched_seconds, completed } = request

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Upsert progress record
      const { error } = await supabase
        .from('user_video_progress')
        .upsert({
          user_id: user.id,
          video_id,
          watched_seconds,
          completed: completed || false,
          completed_at: completed ? new Date().toISOString() : null,
          last_watched_at: new Date().toISOString()
        })

      if (error) throw error

      // If video completed, award XP and check achievements
      if (completed) {
        await this.awardVideoCompletionXP(user.id, video_id)
        await this.checkAndAwardAchievements(user.id)
      }
    } catch (error) {
      console.error('Error updating video progress:', error)
      throw new Error('Failed to update video progress')
    }
  }

  async markVideoComplete(userId: string, videoId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_video_progress')
        .upsert({
          user_id: userId,
          video_id: videoId,
          completed: true,
          completed_at: new Date().toISOString(),
          last_watched_at: new Date().toISOString()
        })

      if (error) throw error

      // Award XP and check achievements
      await this.awardVideoCompletionXP(userId, videoId)
      await this.checkAndAwardAchievements(userId)
    } catch (error) {
      console.error('Error marking video complete:', error)
      throw new Error('Failed to mark video complete')
    }
  }

  async getUserProgress(userId: string): Promise<UserLearningProgress> {
    try {
      // Get basic progress stats
      const { data: progressData, error: progressError } = await supabase
        .from('user_video_progress')
        .select(`
          *,
          learning_videos (
            id,
            title,
            module_id,
            duration,
            difficulty,
            source,
            xp_reward,
            learning_modules (
              id,
              title
            )
          )
        `)
        .eq('user_id', userId)

      if (progressError) throw progressError

      // Get quiz attempts
      const { data: quizData, error: quizError } = await supabase
        .from('user_quiz_attempts')
        .select('*')
        .eq('user_id', userId)

      if (quizError) throw quizError

      // Get achievements
      const { data: achievementsData, error: achievementsError } = await supabase
        .from('user_learning_achievements')
        .select(`
          *,
          learning_achievements (
            *
          )
        `)
        .eq('user_id', userId)

      if (achievementsError) throw achievementsError

      // Calculate stats
      const completedVideos = progressData?.filter(p => p.completed) || []
      const totalXP = completedVideos.reduce((sum, p) => sum + (p.learning_videos?.xp_reward || 0), 0)
      const perfectQuizzes = quizData?.filter(q => q.score === q.total_questions).length || 0
      const fedVideos = completedVideos.filter(p => p.learning_videos?.source === 'federal-reserve').length

      // Calculate current level
      const level = this.calculateLevel(totalXP)
      const xpToNextLevel = level < LEVEL_THRESHOLDS.length - 1 
        ? LEVEL_THRESHOLDS[level] - totalXP 
        : 0

      // Calculate streak (simplified - would need more complex logic for daily streaks)
      const streak = this.calculateStreak(progressData || [])

      // Find next recommended video
      const nextVideo = await this.getNextRecommendedVideo(userId)

      // Find continue learning video
      const continueVideo = await this.getContinueLearningVideo(userId)

      return {
        user_id: userId,
        total_videos_watched: completedVideos.length,
        total_xp_earned: totalXP,
        current_streak: streak,
        longest_streak: streak, // Would need more complex calculation
        modules_completed: new Set(completedVideos.map(p => p.learning_videos?.module_id)).size,
        perfect_quiz_count: perfectQuizzes,
        achievements_earned: achievementsData?.length || 0,
        last_activity_date: progressData?.[0]?.last_watched_at,
        level,
        xp_to_next_level: xpToNextLevel,
        recent_achievements: achievementsData?.slice(0, 3).map(a => a.learning_achievements) || [],
        next_recommended_video: nextVideo,
        continue_learning_video: continueVideo
      }
    } catch (error) {
      console.error('Error fetching user progress:', error)
      throw new Error('Failed to fetch user progress')
    }
  }

  // Quiz Methods
  async getQuizQuestions(videoId: string): Promise<QuizQuestion[]> {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('video_id', videoId)
        .order('order_index')

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching quiz questions:', error)
      throw new Error('Failed to fetch quiz questions')
    }
  }

  async submitQuizAttempt(request: SubmitQuizRequest): Promise<QuizResult> {
    try {
      const { video_id, answers } = request

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Get quiz questions to validate answers
      const questions = await this.getQuizQuestions(video_id)
      if (!questions.length) throw new Error('No quiz questions found')

      // Score the quiz
      let score = 0
      const scoredAnswers = answers.map(answer => {
        const question = questions.find(q => q.id === answer.question_id)
        const isCorrect = question?.correct_answer === answer.selected_answer
        if (isCorrect) score++
        
        return {
          question_id: answer.question_id,
          selected_answer: answer.selected_answer,
          is_correct: isCorrect,
          question
        }
      })

      // Calculate XP earned
      const percentage = Math.round((score / questions.length) * 100)
      let xpEarned = 0
      
      if (percentage === 100) {
        // Perfect score bonus
        xpEarned = XP_REWARDS.QUIZ_PERFECT.MEDIUM // Default to medium difficulty
      } else if (percentage >= 80) {
        // Good score
        xpEarned = Math.round(XP_REWARDS.QUIZ_PERFECT.MEDIUM * 0.7)
      }

      // Save quiz attempt
      const { error } = await supabase
        .from('user_quiz_attempts')
        .insert({
          user_id: user.id,
          video_id,
          score,
          total_questions: questions.length,
          answers: scoredAnswers,
          xp_earned: xpEarned
        })

      if (error) throw error

      // Award XP to user
      if (xpEarned > 0) {
        await this.awardXP(user.id, xpEarned)
      }

      // Check achievements
      await this.checkAndAwardAchievements(user.id)

      return {
        score,
        total_questions: questions.length,
        percentage,
        xp_earned: xpEarned,
        answers: scoredAnswers,
        perfect_score: percentage === 100,
        can_retake: percentage < 80 // Allow retake if score is below 80%
      }
    } catch (error) {
      console.error('Error submitting quiz attempt:', error)
      throw new Error('Failed to submit quiz attempt')
    }
  }

  // Achievement Methods
  async checkAndAwardAchievements(userId: string): Promise<LearningAchievement[]> {
    try {
      const achievements = await this.getAllAchievements()
      const newAchievements: LearningAchievement[] = []

      for (const achievement of achievements) {
        // Check if user already has this achievement
        const { data: existing } = await supabase
          .from('user_learning_achievements')
          .select('id')
          .eq('user_id', userId)
          .eq('achievement_id', achievement.id)
          .single()

        if (existing) continue

        // Check if user meets requirements
        const meetsRequirements = await this.checkAchievementRequirements(userId, achievement)
        if (meetsRequirements) {
          // Award achievement
          await this.awardAchievement(userId, achievement.id)
          newAchievements.push(achievement)
        }
      }

      return newAchievements
    } catch (error) {
      console.error('Error checking achievements:', error)
      return []
    }
  }

  async getUserAchievements(userId: string): Promise<LearningAchievement[]> {
    try {
      const { data, error } = await supabase
        .from('user_learning_achievements')
        .select(`
          *,
          learning_achievements (
            *
          )
        `)
        .eq('user_id', userId)

      if (error) throw error

      return data?.map(item => ({
        ...item.learning_achievements,
        is_earned: true,
        earned_at: item.earned_at
      })) || []
    } catch (error) {
      console.error('Error fetching user achievements:', error)
      return []
    }
  }

  // Private Helper Methods
  private async awardVideoCompletionXP(userId: string, videoId: string): Promise<void> {
    try {
      // Get video details to determine XP reward
      const { data: video } = await supabase
        .from('learning_videos')
        .select('xp_reward')
        .eq('id', videoId)
        .single()

      if (video) {
        await this.awardXP(userId, video.xp_reward)
      }
    } catch (error) {
      console.error('Error awarding video completion XP:', error)
    }
  }

  private async awardXP(userId: string, xpAmount: number): Promise<void> {
    try {
      // Update user's experience in the users table
      const { error } = await supabase
        .from('users')
        .update({
          experience: supabase.sql`experience + ${xpAmount}`
        })
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error awarding XP:', error)
    }
  }

  private async awardAchievement(userId: string, achievementId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_learning_achievements')
        .insert({
          user_id: userId,
          achievement_id
        })

      if (error) throw error

      // Award XP for achievement
      const { data: achievement } = await supabase
        .from('learning_achievements')
        .select('xp_reward')
        .eq('id', achievementId)
        .single()

      if (achievement) {
        await this.awardXP(userId, achievement.xp_reward)
      }
    } catch (error) {
      console.error('Error awarding achievement:', error)
    }
  }

  private async getAllAchievements(): Promise<LearningAchievement[]> {
    try {
      const { data, error } = await supabase
        .from('learning_achievements')
        .select('*')
        .order('xp_reward')

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching achievements:', error)
      return []
    }
  }

  private async checkAchievementRequirements(userId: string, achievement: LearningAchievement): Promise<boolean> {
    const { type, count } = achievement.requirement

    switch (type) {
      case 'videos_watched':
        const { data: videoProgress } = await supabase
          .from('user_video_progress')
          .select('id')
          .eq('user_id', userId)
          .eq('completed', true)
        return (videoProgress?.length || 0) >= count

      case 'perfect_quizzes':
        const { data: perfectQuizzes } = await supabase
          .from('user_quiz_attempts')
          .select('id')
          .eq('user_id', userId)
          .filter('score', 'eq', supabase.sql`total_questions`)
        return (perfectQuizzes?.length || 0) >= count

      case 'daily_streak':
        // Simplified streak calculation - would need more complex logic
        const { data: recentActivity } = await supabase
          .from('user_video_progress')
          .select('last_watched_at')
          .eq('user_id', userId)
          .gte('last_watched_at', new Date(Date.now() - count * 24 * 60 * 60 * 1000).toISOString())
          .order('last_watched_at', { ascending: false })
        return (recentActivity?.length || 0) >= count

      case 'modules_completed':
        const { data: moduleProgress } = await supabase
          .from('user_video_progress')
          .select(`
            learning_videos!inner (
              module_id
            )
          `)
          .eq('user_id', userId)
          .eq('completed', true)

        const completedModules = new Set(moduleProgress?.map(p => p.learning_videos.module_id) || [])
        return completedModules.size >= count

      case 'fed_videos_watched':
        const { data: fedVideos } = await supabase
          .from('user_video_progress')
          .select(`
            learning_videos!inner (
              source
            )
          `)
          .eq('user_id', userId)
          .eq('completed', true)
          .eq('learning_videos.source', 'federal-reserve')
        return (fedVideos?.length || 0) >= count

      case 'all_modules_completed':
        // Check if all published modules are completed
        const { data: allModules } = await supabase
          .from('learning_modules')
          .select('id')
          .eq('is_published', true)

        const { data: userCompletedModules } = await supabase
          .from('user_video_progress')
          .select(`
            learning_videos!inner (
              module_id
            )
          `)
          .eq('user_id', userId)
          .eq('completed', true)

        const completedModuleIds = new Set(userCompletedModules?.map(p => p.learning_videos.module_id) || [])
        return completedModuleIds.size === (allModules?.length || 0)

      default:
        return false
    }
  }

  private calculateLevel(totalXP: number): number {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (totalXP >= LEVEL_THRESHOLDS[i]) {
        return i + 1
      }
    }
    return 1
  }

  private calculateStreak(progressData: any[]): number {
    // Simplified streak calculation
    // In a real implementation, this would check for consecutive days of activity
    const recentDays = new Set()
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    
    progressData.forEach(progress => {
      const activityDate = new Date(progress.last_watched_at)
      if (activityDate >= oneWeekAgo) {
        recentDays.add(activityDate.toDateString())
      }
    })

    return recentDays.size
  }

  private async getNextRecommendedVideo(userId: string): Promise<LearningVideo | null> {
    try {
      // Find the first incomplete video in the user's learning path
      const { data, error } = await supabase
        .from('learning_videos')
        .select(`
          *,
          learning_modules (
            id,
            title,
            difficulty
          )
        `)
        .eq('is_published', true)
        .not('id', 'in', supabase.sql`
          (SELECT video_id FROM user_video_progress WHERE user_id = ${userId} AND completed = true)
        `)
        .order('module_id')
        .order('order_index')
        .limit(1)

      if (error) throw error
      return data?.[0] || null
    } catch (error) {
      console.error('Error getting next recommended video:', error)
      return null
    }
  }

  private async getContinueLearningVideo(userId: string): Promise<LearningVideo | null> {
    try {
      // Find the most recently watched incomplete video
      const { data, error } = await supabase
        .from('user_video_progress')
        .select(`
          *,
          learning_videos (
            *,
            learning_modules (
              id,
              title,
              difficulty
            )
          )
        `)
        .eq('user_id', userId)
        .eq('completed', false)
        .gt('watched_seconds', 0)
        .order('last_watched_at', { ascending: false })
        .limit(1)

      if (error) throw error
      return data?.[0]?.learning_videos || null
    } catch (error) {
      console.error('Error getting continue learning video:', error)
      return null
    }
  }
}

// Create and export singleton instance
export const learningService = new LearningService()
export default LearningService
